/**
 * ReferenceDropZone Component
 * Drag and drop area for uploading reference images
 */

import { useState, useCallback, useRef } from "react";
import { Upload, ImagePlus } from "lucide-react";
import { cn } from "@/lib/utils";

interface ReferenceDropZoneProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
}

export function ReferenceDropZone({
  onFilesSelected,
  disabled = false,
}: ReferenceDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith("image/"),
      );

      if (files.length > 0) {
        onFilesSelected(files);
      }
    },
    [disabled, onFilesSelected],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length > 0) {
        onFilesSelected(files);
      }
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFilesSelected],
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "relative rounded-[8px] border-2 border-dashed transition-all duration-200 cursor-pointer",
        "flex flex-col items-center justify-center py-12 px-6",
        isDragging
          ? "border-primary bg-primary/5"
          : "border-white/20 hover:border-white/30 hover:bg-white/[0.02]",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled}
        data-testid="asset-file-input"
      />

      <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-4">
        {isDragging ? (
          <ImagePlus className="w-6 h-6 text-primary" />
        ) : (
          <Upload className="w-6 h-6 text-muted-foreground" />
        )}
      </div>

      <p className="text-sm text-foreground font-medium mb-1">
        {isDragging
          ? "Drop images here"
          : "Drop images here or click to browse"}
      </p>
      <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB each</p>
    </div>
  );
}
